import {
  ADD_CERTIFICATE_URL,
  CERTIFICATES_URL,
  COPY_PASSWORD_URL,
  DELETE_CERTIFICATE_URL,
  DELETE_LICENSE_URL,
  DELETE_PASSWORD_URL,
  DOWNLOAD_CERTIFICATE_URL,
  GENERATE_CSR_URL,
  GET_REQUESTS_URL,
  IMPORT_ALLOCATION_FILE_URL,
  LICENSES_URL,
  PASSWORDS_URL,
  REPLACE_CERTIFICATE_URL,
  UPDATE_LICENSE_URL,
  UPDATE_PASSWORD_URL,
  VALIDATE_CERTIFICATE_URL
} from '../../constants/ApiUri';
import {
  AddCertificateFormValues,
  UploadCertificateFormValues
} from '../../types/resources/Certificate';
import {
  AddCertificatePayload,
  AddLicense,
  CertProperties,
  License,
  LicenseResultSet,
  ReplaceCertificate,
  RequestResponse,
  UploadCertificatePayload,
  ValidateLicense,
  UpdatePasswordPayload
} from '../../types/resources/State';
import { Http, HttpResponse } from 'vrlcm-gui-utils';

import { getFileName } from '../../utils/common';
import { saveAs } from 'file-saver';
import { Password } from 'vrlcm-gui-types';

export default class ManageResource {
  public static AddCertificate = (
    certificate: AddCertificatePayload
  ): Promise<HttpResponse> => {
    let hostNames: string[] = [];
    if (certificate.hostname !== '' && certificate.hostname !== undefined) {
      hostNames = certificate.hostname.split(',');
    }
    let ipS: string[] = [];
    if (certificate.ipAddress !== '' && certificate.ipAddress !== undefined) {
      ipS = certificate.ipAddress.split(',');
    }
    const body = {
      alias: certificate.name,
      ip: ipS,
      host: hostNames,
      cN: certificate.commonName,
      oU: certificate.organizationUnit,
      size: certificate.size,
      o: certificate.organization,
      l: certificate.locality,
      sT: certificate.state,
      c: certificate.countryCode
    };
    return Http.post(ADD_CERTIFICATE_URL, body).then(
      res => res
    );
  };
  public static AddPassword = (password: Password): Promise<Password> => {
    return Http.post(PASSWORDS_URL, password).then(res => res.data);
  };
  public static UploadCertificate = (
    data: UploadCertificatePayload
  ): Promise<HttpResponse> => {
    const formData: FormData = new FormData();
    formData.append('alias', data.name);
    formData.append('passphrase', data.passPhrase);
    formData.append('privateKey', data.privateKey);
    formData.append('certificateChain', data.certificateChain);
    return Http.post(CERTIFICATES_URL, formData).then(res => res);
  };
  public static ValidateCertificate = (
    fileData: UploadCertificateFormValues
  ): Promise<HttpResponse> => {
    const formData = new FormData();
    formData.append('bundle', fileData.file, fileData.file.name);
    formData.append('passphrase', fileData.passPhrase);
    return Http.post(VALIDATE_CERTIFICATE_URL, formData).then(res => res);
  };
  public static ImportAllocationFile = (fileData: {
    file: string | Blob;
  }): Promise<{}> => {
    const formData = new FormData();
    formData.append('allocationFile', fileData.file);
    return Http.post(IMPORT_ALLOCATION_FILE_URL, formData).then(
      res => res.data
    );
  };
  public static DeletePassword = (password: Password): Promise<null> => {
    return Http.delete(DELETE_PASSWORD_URL(password.vmid)).then(
      res => res.data
    );
  };
  public static UpdatePassword = (
    passwordVMID: string,
    payload: UpdatePasswordPayload
  ): Promise<any> => {
    return Http.patch(UPDATE_PASSWORD_URL(passwordVMID), payload).then(
      res => res.data
    );
  };
  public static ChangePassword = (password: Password): Promise<null> => {
    return new Promise((resolve, reject) => {
      fetch(UPDATE_PASSWORD_URL(password.vmid), {
        method: 'PUT',
        body: JSON.stringify(password),
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(response => {
          if (response.status === 200) {
            resolve(null);
          } else {
            reject(null);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  };
  public static replaceCertificate = (
    payload: ReplaceCertificate
  ): Promise<Response> => {
    return Http.post(REPLACE_CERTIFICATE_URL(payload.vmid), payload).then(
      res => res.data
    );
  };
  public static CopyPassword = (
    password: Password,
    vmid: string
  ): Promise<null> => {
    return new Promise((resolve, reject) => {
      fetch(COPY_PASSWORD_URL(vmid), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      })
        .then(response => {
          if (response.ok) {
            response.json().then(json => {
              resolve(json);
            });
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  };
  public static DeleteLicense = (license: License): Promise<null> => {
    return Http.delete(DELETE_LICENSE_URL(license.vmid)).then(res => res.data);
  };
  public static DeleteCertificate = (
    certificate: CertProperties
  ): Promise<null> => {
    return Http.delete(DELETE_CERTIFICATE_URL(certificate.vmid)).then(
      res => res.data
    );
  };
  public static DownloadCertificate = (alias: string): Promise<any> => {
    return Http.get(DOWNLOAD_CERTIFICATE_URL(alias)).then(res => {
      if (res.status === 200) {
        const fileName = getFileName(res);
        const fileDetails = {
          fileContent: new Blob([res.data], {
            type: 'text/plain'
          }),
          fileName
        };

        saveAs(fileDetails.fileContent, fileDetails.fileName);
      }
      return res.data;
    });
  };
  public static GenerateCsr = async (
    certificate: AddCertificateFormValues
  ): Promise<HttpResponse> => {
    let hostNames: string[] = [];
    if (certificate.hostname !== '' && certificate.hostname !== undefined) {
      hostNames = certificate.hostname.split(',');
    }
    let ipS: string[] = [];
    if (certificate.ipAddress !== '' && certificate.ipAddress !== undefined) {
      ipS = certificate.ipAddress.split(',');
    }
    const body = {
      ip: ipS,
      host: hostNames,
      cN: certificate.commonName,
      oU: certificate.organizationUnit,
      size: certificate.size,
      o: certificate.organization,
      l: certificate.locality,
      sT: certificate.state,
      c: certificate.countryCode
    };
    const headers = {
      'Content-Type': 'application/json'
    };
    return await Http.post(GENERATE_CSR_URL, body, {
      headers
    });
  };
  public static updateLicense = (
    vmid: string,
    license: AddLicense
  ): Promise<License> => {
    return Http.put(UPDATE_LICENSE_URL(vmid), license).then(res => res.data);
  };
  public static AddLicense = (
    license: LicenseResultSet
  ): Promise<ValidateLicense> => {
    return Http.post(LICENSES_URL, license).then(res => res.data);
  };
  public static getRequest = async (
    requestId: string
  ): Promise<RequestResponse> => {
    const request = await Http.get<RequestResponse>(
      `${GET_REQUESTS_URL}/${requestId}`
    );
    return request.data;
  };
}
